import { create } from 'zustand'
import { apiFetch } from '../services/api'

export interface UserProfile {
  id: string
  email: string
  displayName: string | null
  avatarUrl: string | null
  birthday: string | null
  birthTime: string | null
  birthTimeSkipped: boolean | null
  birthLocation: string | null
  birthLatitude: string | null
  birthLongitude: string | null
  natalChart: Record<string, unknown> | null
  membershipTier: 'free' | 'paid'
  membershipExpiresAt: string | null
  stripeSubscriptionId: string | null
  tierPreview: 'free' | 'paid' | null
  role: 'user' | 'beta_tester' | 'admin' | 'practitioner'
  /** Persisted appearance theme id (paid only; free is enforced null server-side). */
  theme: string | null
  /** Persisted accent hue (0–360) for future accent theming; null = default. */
  accentHue: number | null
  onboardingCompletedAt: string | null
  termsAcceptedAt: string | null
  createdAt: string
  settings: {
    backgroundColor: string
    fontSize: string
    preferredDeckId: string
  } | null
}

interface UserState {
  profile: UserProfile | null
  loading: boolean
  error: string | null
  fetchProfile: () => Promise<void>
  updateProfile: (data: Partial<UserProfile>) => Promise<void>
  updateSettings: (data: Record<string, string>) => Promise<void>
  acceptTerms: () => Promise<void>
  clear: () => void
}

export const useUserStore = create<UserState>((set, get) => ({
  profile: null,
  loading: false,
  error: null,

  fetchProfile: async () => {
    set({ loading: true, error: null })
    try {
      const res = await apiFetch('/users/me')
      if (!res.ok) {
        // Don't crash on auth or network errors -- just clear profile
        set({ profile: null, loading: false })
        return
      }
      const data = await res.json()
      set({ profile: data.user, loading: false })
    } catch (err) {
      set({ error: (err as Error).message, loading: false })
    }
  },

  updateProfile: async (data) => {
    const res = await apiFetch('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update profile')
    const result = await res.json()
    set((state) => ({
      profile: state.profile ? { ...state.profile, ...result.user } : null,
    }))
  },

  updateSettings: async (data) => {
    const res = await apiFetch('/users/me/settings', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update settings')
    const result = await res.json()
    set((state) => ({
      profile: state.profile
        ? { ...state.profile, settings: result.settings }
        : null,
    }))
  },

  acceptTerms: async () => {
    const res = await apiFetch('/users/me/accept-terms', { method: 'POST' })
    if (!res.ok) throw new Error('Failed to accept terms')
    const data = await res.json()
    set((state) => ({
      profile: state.profile
        ? { ...state.profile, termsAcceptedAt: data.terms_accepted_at }
        : state.profile,
    }))
  },

  clear: () => set({ profile: null, loading: false, error: null }),
}))
